import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import TableChartIcon from '@mui/icons-material/TableChart';
import PuffLoader from 'react-spinners/PuffLoader';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import SendPaymentMessageModal from '../ClientDetails/Modals/SendPaymentMessageModal';
import WhatsAppMessagesService from '../../Services/whatsapp-messages.service';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import useReportingFollowUp from './useReportingFollowUp';
import {
    FOLLOW_UP_SECTIONS,
    MANUAL_KEYS,
    formatPeriodLabel,
    getMonthName,
    getSectionReportingType,
    reportingTypeLabel,
    todayDateString,
} from './reportingFollowUpConstants';
import VatSection from './SubComponents/VatSection';
import AdvanceSection from './SubComponents/AdvanceSection';
import WithholdingSection from './SubComponents/WithholdingSection';
import PersonalNiSection from './SubComponents/PersonalNiSection';
import NiWithholdingSection from './SubComponents/NiWithholdingSection';
import { AmountInput, FollowUpDatePicker } from './SubComponents/SectionParts';
import {
    buildFollowUpReport,
    collectFollowUpPayments,
    downloadFollowUpExcel,
    downloadFollowUpPdf,
    printFollowUp,
} from './reportingFollowUpExport';
import styles from './ReportingFollowUp.module.scss';

const toYearMonth = (date) => {
    const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const ReportingFollowUpModal = ({ open, onClose, client, user, token, defaultDate }) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const language = i18n.language;

    const [{ year, month }, setYearMonth] = useState(() => toYearMonth(defaultDate));

    useEffect(() => {
        if (open) setYearMonth(toYearMonth(defaultDate));
        // Only re-seed when the modal opens, not while the user browses months.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const state = useReportingFollowUp({ open, client, user, token, year, month });
    const { periods, followUp } = state;
    const sectionReportingType = (section) => reportingTypeLabel(getSectionReportingType(client, section), t);

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        return Array.from({ length: 8 }, (_, i) => current + 1 - i).map((y) => ({ value: y, label: String(y) }));
    }, []);

    const monthOptions = useMemo(
        () => Array.from({ length: 12 }, (_, i) => i + 1).map((m) => ({
            value: m,
            label: `${String(m).padStart(2, '0')} - ${getMonthName(m, language)}`,
        })),
        [language]
    );

    const trackingProps = (section, partnerId = null) => ({
        tracking: state.getTracking(section, partnerId),
        onTrackingChange: (field, value) => state.setTrackingField(section, partnerId, field, value),
        onManualChange: (name, value) => state.setManualValue(section, partnerId, name, value),
        direction: dir,
    });

    const advancePartnerId = client?.isPartners ? state.selectedPartnerId : null;
    const withholdingBooks = followUp?.withholdingBooks ?? null;

    const copyWithholdingBooks = () => {
        if (!withholdingBooks) return;
        const keys = MANUAL_KEYS.WITHHOLDING;
        state.setManualValues(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING, null, {
            [keys.reportRecipients]: String(withholdingBooks.numberOfRecipients ?? 0),
            [keys.reportTotalPayments]: String(Math.round(withholdingBooks.totalPayments ?? 0)),
            [keys.reportTotalWithholding]: String(Math.round(withholdingBooks.totalWithholding ?? 0)),
        });
    };

    const [exportingPdf, setExportingPdf] = useState(false);
    const [messageOpen, setMessageOpen] = useState(false);
    const exportDisabled = state.loading || !followUp || exportingPdf;
    const partnerName = state.partners?.find((p) => p.id === advancePartnerId)?.name;
    const { handleSendTextMessageToChat } = useHamburgerMenu() || {};

    const buildReport = () => buildFollowUpReport({
        t,
        language,
        client,
        year,
        month,
        state,
        followUp,
        partnerName,
    });

    const messagePayments = messageOpen
        ? collectFollowUpPayments({ t, client, state, followUp, partnerName })
        : null;

    const handleSendMessageToChat = async (message) => {
        if (typeof handleSendTextMessageToChat !== 'function' || !client?.caseId) {
            throw new Error('Chat is not available');
        }
        await handleSendTextMessageToChat(message, client.caseId, client.name || '');
    };

    const handleDownloadPdf = async () => {
        setExportingPdf(true);
        try {
            await downloadFollowUpPdf(buildReport(), t, token);
        } catch (error) {
            console.error('Error generating reporting follow-up PDF:', error);
            toast.error(t('ErrorGeneratingPDF'));
        } finally {
            setExportingPdf(false);
        }
    };

    const handleDownloadExcel = () => {
        try {
            downloadFollowUpExcel(buildReport(), t);
        } catch (error) {
            console.error('Error generating reporting follow-up Excel:', error);
            toast.error(t('ErrorGeneratingExcel'));
        }
    };

    const handlePrint = () => printFollowUp(buildReport(), t);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            dir={dir}
            PaperProps={{
                className: styles.paper,
                sx: {
                    maxWidth: 1280,
                    width: 'min(1280px, calc(100vw - 32px))',
                },
            }}
            BackdropProps={{ sx: { background: 'rgba(20, 30, 43, 0.45)', backdropFilter: 'blur(6px)' } }}
        >
            <DialogTitle className={styles.header}>
                <span>{t('ReportingFollowUp')}</span>
                <div className={styles.headerEnd}>
                    {client ? (
                        <span className={styles.headerClient}>
                            {client.name} · {client.caseId}
                        </span>
                    ) : null}
                    <div className={styles.exportActions}>
                        <Button
                            className={`${styles.exportButton} ${styles.messageButton}`}
                            variant="contained"
                            disableElevation
                            startIcon={<ChatIcon />}
                            onClick={() => setMessageOpen(true)}
                            disabled={exportDisabled}
                        >
                            {t('SendPaymentMessage')}
                        </Button>
                        <Button
                            className={styles.exportButton}
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadPdf}
                            disabled={exportDisabled}
                        >
                            {exportingPdf ? t('GeneratingPDF') : t('DownloadPDFFile')}
                        </Button>
                        <Button
                            className={styles.exportButton}
                            variant="outlined"
                            startIcon={<TableChartIcon />}
                            onClick={handleDownloadExcel}
                            disabled={exportDisabled}
                        >
                            {t('DownloadExcelFile')}
                        </Button>
                        <Button
                            className={styles.exportButton}
                            variant="outlined"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            disabled={exportDisabled}
                        >
                            {t('PrintReport')}
                        </Button>
                    </div>
                </div>
            </DialogTitle>

            <DialogContent className={styles.scroll}>
                <div className={styles.topBar}>
                    <div className={styles.gridField}>
                        <span className={styles.fieldLabel}>{t('TaxYear')}</span>
                        <AutocompleteSelectFilter
                            options={yearOptions}
                            selectedValues={yearOptions.find((o) => o.value === year) || { value: year, label: String(year) }}
                            onChange={(e, option) => option && setYearMonth((prev) => ({ ...prev, year: option.value }))}
                            placeholder={t('TaxYear')}
                            isMultiple={false}
                            direction={dir}
                            portalMenu
                        />
                    </div>
                    <div className={styles.gridField}>
                        <span className={styles.fieldLabel}>{t('FollowUpReportMonth')}</span>
                        <AutocompleteSelectFilter
                            options={monthOptions}
                            selectedValues={monthOptions.find((o) => o.value === month) || null}
                            onChange={(e, option) => option && setYearMonth((prev) => ({ ...prev, month: option.value }))}
                            placeholder={t('FollowUpReportMonth')}
                            isMultiple={false}
                            direction={dir}
                            portalMenu
                        />
                    </div>
                    <div className={styles.gridField}>
                        <span className={styles.fieldLabel}>{t('FollowUpOutstandingFeeBalance')}</span>
                        <AmountInput
                            className={styles.feeInput}
                            value={state.outstandingFeeBalance}
                            onChange={state.setOutstandingFeeBalance}
                            ariaLabel={t('FollowUpOutstandingFeeBalance')}
                            disabled={state.loading || !followUp}
                        />
                    </div>
                    <div className={styles.materialReceived}>
                        <Button
                            className={styles.materialButton}
                            variant="outlined"
                            onClick={() => state.setMaterialReceivedDate(todayDateString())}
                            disabled={state.loading || !followUp}
                        >
                            {t('MaterialReceivedAtOffice')}
                        </Button>
                        <div className={styles.materialDate} title={t('FollowUpMaterialReceivedDate')}>
                            <FollowUpDatePicker
                                value={state.materialReceivedDate}
                                onChange={state.setMaterialReceivedDate}
                                ariaLabel={t('FollowUpMaterialReceivedDate')}
                                disabled={state.loading || !followUp}
                            />
                        </div>
                    </div>
                </div>

                {state.loading && !followUp ? (
                    <div className={styles.loader}>
                        <PuffLoader size={80} loading />
                    </div>
                ) : (
                    <div className={styles.sectionsGrid} style={{ opacity: state.loading ? 0.5 : 1 }}>
                        <VatSection
                            vat={state.vat}
                            periodLabel={formatPeriodLabel(periods.vat, language)}
                            fileNumber={followUp?.vatFileNumber}
                            reportingType={sectionReportingType(FOLLOW_UP_SECTIONS.VAT)}
                            onReportChange={state.setVatReportField}
                            onCopy={state.copyVatBooks}
                            onReport={state.reportVatPeriod}
                            reportDisabled={state.saving || state.loading}
                            {...trackingProps(FOLLOW_UP_SECTIONS.VAT)}
                        />
                        <AdvanceSection
                            advance={state.advance}
                            periodLabel={formatPeriodLabel(periods.advance, language)}
                            fileNumber={followUp?.taxFileNumber}
                            reportingType={sectionReportingType(FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE)}
                            isPartnership={!!client?.isPartners}
                            partners={state.partners}
                            selectedPartnerId={state.selectedPartnerId}
                            onPartnerChange={state.setSelectedPartnerId}
                            onReportChange={state.setAdvanceReportField}
                            onCopy={state.copyAdvanceBooks}
                            onReport={state.reportAdvancePeriod}
                            reportDisabled={state.saving || state.loading}
                            {...trackingProps(FOLLOW_UP_SECTIONS.INCOME_TAX_ADVANCE, advancePartnerId)}
                        />
                        <WithholdingSection
                            books={withholdingBooks}
                            periodLabel={formatPeriodLabel(periods.withholding, language)}
                            fileNumber={followUp?.deductionsFileNumber}
                            reportingType={sectionReportingType(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING)}
                            onCopy={copyWithholdingBooks}
                            isReported={state.withholdingReported}
                            onReport={state.reportWithholdingPeriod}
                            reportDisabled={state.saving || state.loading}
                            {...trackingProps(FOLLOW_UP_SECTIONS.INCOME_TAX_WITHHOLDING)}
                        />
                        <PersonalNiSection
                            ownerName={followUp?.businessOwnerName || client?.businessOwnerName || client?.name}
                            spouseName={followUp?.spouseName || client?.spouseName}
                            periodLabel={formatPeriodLabel(periods.nationalInsurance, language)}
                            fileNumber={followUp?.taxFileNumber}
                            autoValues={state.personalNiAutoValues}
                            {...trackingProps(FOLLOW_UP_SECTIONS.PERSONAL_NATIONAL_INSURANCE)}
                        />
                        <NiWithholdingSection
                            periodLabel={formatPeriodLabel(periods.nationalInsurance, language)}
                            fileNumber={followUp?.deductionsFileNumber}
                            {...trackingProps(FOLLOW_UP_SECTIONS.NATIONAL_INSURANCE_WITHHOLDING)}
                        />
                    </div>
                )}
            </DialogContent>

            <DialogActions className={styles.actions}>
                <Button className={styles.cancel} variant="contained" disableElevation onClick={onClose}>
                    {t('cancel')}
                </Button>
                <Button
                    className={styles.save}
                    variant="contained"
                    disableElevation
                    onClick={() => state.save()}
                    disabled={!state.isDirty || state.saving || state.loading}
                >
                    {state.saving ? t('Loading') : t('save')}
                </Button>
            </DialogActions>

            <SendPaymentMessageModal
                open={messageOpen}
                onClose={() => setMessageOpen(false)}
                t={t}
                direction={dir}
                clientName={client?.name || ''}
                accountantName={user?.name || ''}
                caseId={client?.caseId || ''}
                periodLabel={`${getMonthName(month, language)} ${year}`}
                payments={messagePayments || []}
                onSend={handleSendMessageToChat}
                onSendWhatsApp={WhatsAppMessagesService.sendPaymentReminder}
            />
        </Dialog>
    );
};

ReportingFollowUpModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    client: PropTypes.object,
    user: PropTypes.object,
    token: PropTypes.string,
    defaultDate: PropTypes.instanceOf(Date),
};

export default ReportingFollowUpModal;
